import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import { affichageDeLecture, LIEN_BOUTON_PLEIN, RelectureEchouee } from "./coloriage-commun";
import { CarteAgent, CarteDemandes } from "./coloriage-demandes";
import { NouveauCahierPanneau } from "./coloriage-nouveau";
import { BADGE_VERT } from "./coloriage-points";
import { useAccueilColoriage } from "./coloriage-requetes";
import { etatCahierCourt, formatCourt, modeleCourt, poids } from "./coloriage-textes";
import type { CahierResume } from "./contract";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur } from "./states";

/**
 * L'accueil du module coloriage (`/coloriage`) : l'agent du PC et les demandes (ticket 3,
 * #152), la table des cahiers, puis la porte des variantes du prompt (ticket 4, #153).
 * Depuis #271, « Nouveau cahier » cree un brouillon dans la console (`coloriage-nouveau.tsx`),
 * dont le sommaire s'ecrit sur la page du cahier.
 *
 * La maquette (`ColoriageAccueil.dc.html`) liste les variantes et leurs taux ici meme ;
 * `GET /api/coloriage` ne les rend pas, et les redemander a chaque relecture de l'accueil
 * (toutes les 5 s pendant un rendu) pour trois lignes ne se justifie pas : l'accueil renvoie
 * a l'ecran des variantes, qui les lit. Tant qu'une demande attend ou tourne, la page se
 * relit toutes les 5 s (`useAccueilColoriage`).
 */

/** La commande qui pousse un cahier depuis le PC (`scripts/coloriage.ps1`, ticket 2). */
export const COMMANDE_POUSSER = "./scripts/coloriage.ps1 console pousser <dossier>";

export function ColoriageAccueilPage({ maintenant = Date.now() }: { maintenant?: number }) {
  const lecture = useAccueilColoriage();
  // Jamais l'ecran d'erreur et la table ensemble (`affichageDeLecture`).
  const affichage = affichageDeLecture(lecture);
  const [panneau, setPanneau] = useState<"ferme" | "ouvert" | "annule">("ferme");
  const bouton = useRef<HTMLButtonElement>(null);

  // Ferme sans creer : le focus revient au bouton qui l'a ouvert.
  useEffect(() => {
    if (panneau === "annule") bouton.current?.focus();
  }, [panneau]);

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-2xl font-semibold">Coloriage</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Juge chaque version en taille réelle. Le PC rend, la console décide.
          </p>
        </div>
        {affichage.contenu ? (
          <button
            aria-expanded={panneau === "ouvert"}
            className={cn(LIEN_BOUTON_PLEIN, "max-md:min-h-11")}
            onClick={() => setPanneau("ouvert")}
            ref={bouton}
            type="button"
          >
            Nouveau cahier
          </button>
        ) : null}
      </header>
      {/* Sous l'en-tete, pres du bouton qui l'ouvre : la page est longue, et le panneau ne doit
          pas s'ouvrir sous la table des cahiers, hors de la vue. */}
      {panneau === "ouvert" && affichage.contenu ? (
        <NouveauCahierPanneau fermer={() => setPanneau("annule")} />
      ) : null}
      {lecture.isPending ? <Chargement quoi="des cahiers" /> : null}
      {affichage.ecranDErreur ? <EcranDErreur error={lecture.error} quoi="les cahiers" /> : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {affichage.contenu && lecture.data ? (
        <>
          {/* L'agent a gauche, les demandes a droite ; l'un sous l'autre sous `lg`. La colonne
              unique est bornee (`minmax(0,1fr)`) : en largeur automatique, la table des demandes
              (36rem au moins) elargissait la page a 642 px au telephone (vu le 3 octobre 2026). */}
          <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-5 lg:grid-cols-[21rem_minmax(0,1fr)]">
            <CarteAgent
              agent={lecture.data.agent}
              demandes={lecture.data.demandes}
              maintenant={maintenant}
            />
            <CarteDemandes
              cahiersClos={
                new Set(
                  lecture.data.cahiers
                    .filter((cahier) => cahier.etat === "clos")
                    .map((cahier) => cahier.id),
                )
              }
              demandes={lecture.data.demandes}
              maintenant={maintenant}
            />
          </div>
          <Cahiers cahiers={lecture.data.cahiers} />
          <CarteVariantes />
        </>
      ) : null}
    </ConsoleShell>
  );
}

/** La porte de l'ecran des variantes : c'est lui qui les lit, avec leurs taux. */
function CarteVariantes() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Variantes du prompt</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Une variante change une phrase du prompt, puis se compare à la référence sur les mêmes
          pages et les mêmes graines.
        </p>
        <Link
          className="rounded-md border border-input px-3 py-1.5 text-sm hover:bg-muted"
          to="/coloriage/variantes"
        >
          Toutes les variantes
        </Link>
      </CardContent>
    </Card>
  );
}

function Cahiers({ cahiers }: { cahiers: readonly CahierResume[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base" id="coloriage-cahiers">
          Cahiers
        </CardTitle>
      </CardHeader>
      <CardContent>
        {cahiers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun cahier pour l'instant. Crée-en un ici par « Nouveau cahier », ou pousse un cahier
            depuis le PC :{" "}
            <code className="break-all rounded bg-muted px-1.5 py-0.5 text-foreground">
              {COMMANDE_POUSSER}
            </code>
          </p>
        ) : (
          // Neuf colonnes : sur telephone la table defile a l'horizontale plutot que de
          // tasser les nombres les uns sur les autres.
          <div className="overflow-x-auto">
            <table
              aria-labelledby="coloriage-cahiers"
              className="w-full min-w-[46rem] border-collapse text-sm"
            >
              <thead>
                <tr className="text-left text-xs font-semibold text-muted-foreground">
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Cahier
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Modèle
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Format
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Pages
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Versions
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Jugées
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Choisies
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    Images
                  </th>
                  <th className="px-3 pb-2 font-semibold" scope="col">
                    État
                  </th>
                </tr>
              </thead>
              <tbody>
                {cahiers.map((cahier) => (
                  <LigneCahier cahier={cahier} key={cahier.id} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LigneCahier({ cahier }: { cahier: CahierResume }) {
  const cellule = "border-t border-border px-3 py-2.5 align-middle";
  const nombre = cn(cellule, "tabular-nums");
  const ouEnEst = etatCahierCourt(cahier);
  return (
    <tr>
      <td className={cellule}>
        <Link
          className="font-semibold underline-offset-2 hover:underline"
          params={{ id: String(cahier.id) }}
          to="/coloriage/cahiers/$id"
        >
          {cahier.titre}
        </Link>
        {cahier.sous_titre === null ? null : (
          <span className="block text-xs text-muted-foreground">{cahier.sous_titre}</span>
        )}
      </td>
      <td className={cellule}>{modeleCourt(cahier.modele)}</td>
      <td className={cn(cellule, "whitespace-nowrap")}>{formatCourt(cahier.format)}</td>
      <td className={nombre}>{cahier.pages}</td>
      <td className={nombre}>{cahier.versions}</td>
      <td className={cn(nombre, "whitespace-nowrap")}>
        {cahier.jugees} sur {cahier.versions}
      </td>
      <td className={cn(nombre, "whitespace-nowrap")}>
        {cahier.choisies} sur {cahier.pages}
      </td>
      <td className={cn(nombre, "whitespace-nowrap")}>{poids(cahier.octets_images)}</td>
      <td className={cellule}>
        <BadgeEtatCahier etat={cahier.etat} />
        {ouEnEst === null ? null : (
          <span className="mt-1 block whitespace-nowrap text-xs text-muted-foreground">
            {ouEnEst}
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * Le ton du badge d'un etat. Ouvert en vert, comme sur la maquette ; clos en gris. Brouillon
 * (#271) prend le violet de l'etape « build » du portefeuille (`--etape-build`, `theme.css`
 * section 2) : une teinte deja mesuree dans les deux themes, qui dit deja « en construction »
 * dans la console (les Histoires y peignent un livre ouvert, qu'on ecrit encore). Aucune des
 * trois couleurs de jugement : un brouillon n'a rien a voir avec « bonne », « défaut » ou « à
 * jeter », et l'ambre aurait lu « défaut » dans un module ou il le veut dire.
 *
 * Remesure le 3 octobre 2026 dans Chromium, sur le CSS construit (e2e `coloriage.spec.ts`,
 * qui echoue sous 4,5:1) : dans la table de l'accueil, sur une carte, 6,29:1 en clair et
 * 6,87:1 en sombre ; dans l'en-tete d'un brouillon, sur le fond de page, 5,89:1 et 7,77:1.
 */
const TON_ETAT_CAHIER: Readonly<Record<CahierResume["etat"], string>> = {
  brouillon: "bg-etape-build/15 text-etape-build",
  ouvert: BADGE_VERT,
  clos: "bg-muted text-muted-foreground",
};

/** L'etat d'un cahier, en badge : `brouillon`, `ouvert`, `clos`. */
export function BadgeEtatCahier({ etat }: { etat: CahierResume["etat"] }) {
  return (
    <span
      className={cn(
        "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold",
        TON_ETAT_CAHIER[etat],
      )}
    >
      {etat}
    </span>
  );
}
